import React from "react";
import { isNodeEdge, NodeOrEdge } from "@hex-insights/app-modules";
import { dateDiff, isAfter, isBefore, stringToLocalDate, useCount } from "@hex-insights/core";
import { EventCalendarItem } from "../../../Components/EventCalendar";
import {
	AcademicTerm,
	AcademicTermOrderField,
	AcademicTermsDatesListQuery,
	isBetweenTimes,
	OrderDirection,
	useAcademicTermsDatesListQuery,
} from "../../../Utilities";
import { AcademicTermFormat } from "../../Format";
import { getEnrolledInAcademicTermOnDateFilter } from "../StudentAcademicTermEnrollment";

export function isActive(academicTerm: Pick<AcademicTerm, "startDate" | "endDate">, date = new Date()) {
	const startDate = stringToLocalDate(academicTerm.startDate, "day");
	const endDate = stringToLocalDate(academicTerm.endDate, "day");
	return isBetweenTimes(date, startDate, endDate, "day");
}

export function isPast(academicTerm: Pick<AcademicTerm, "endDate">, date = new Date()) {
	const endDate = stringToLocalDate(academicTerm.endDate, "day");
	return isAfter(date, endDate, "day");
}

export function isFuture(academicTerm: Pick<AcademicTerm, "startDate">, date = new Date()) {
	const startDate = stringToLocalDate(academicTerm.startDate, "day");
	return isBefore(date, startDate, "day");
}

export type UseAcademicTermsNavigationArgs = {
	defaultSelectedTermFallback?: "closest" | "future" | "past";
};

export function useAcademicTermsNavigation({
	defaultSelectedTermFallback = "closest",
}: UseAcademicTermsNavigationArgs = {}) {
	const { loading, data } = useAcademicTermsDatesListQuery({
		variables: {
			order: { field: AcademicTermOrderField.StartDate, direction: OrderDirection.Asc },
		},
	});

	const {
		count: index,
		increment: selectNextAcademicTerm,
		decrement: selectPreviousAcademicTerm,
		setCount: setIndex,
	} = useCount(0, { min: 0, max: data?.academicTermConnection.edges.length ?? 0 });

	const { academicTerms, currentAcademicTerm } = React.useMemo(() => {
		if (!data) {
			return { academicTerms: [], currentAcademicTerm: null };
		}

		const academicTerms = [];
		let currentAcademicTerm: AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"] | null = null;
		let currentAcademicTermIndex: number | null = null;
		let previousAcademicTermIndex: number | null = null;
		let nextAcademicTermIndex: number | null = null;
		const today = new Date();
		for (let i = 0; i < data.academicTermConnection.edges.length; i++) {
			const academicTerm = data.academicTermConnection.edges[i].node;
			academicTerms.push(academicTerm);
			if (isActive(academicTerm, today)) {
				currentAcademicTerm = academicTerm;
				currentAcademicTermIndex = i;
				setIndex(i);
			} else if (isPast(academicTerm, today)) {
				previousAcademicTermIndex = i;
			} else if (nextAcademicTermIndex === null && isFuture(academicTerm, today)) {
				nextAcademicTermIndex = i;
			}
		}

		// using currentAcademicTerm as check results in TypeScript thinking that currentAcademicTerm is of type `null` (only)
		if (currentAcademicTermIndex === null) {
			if (
				defaultSelectedTermFallback === "closest" &&
				previousAcademicTermIndex !== null &&
				nextAcademicTermIndex !== null
			) {
				const previousAcademicTerm = academicTerms[previousAcademicTermIndex];
				const nextAcademicTerm = academicTerms[nextAcademicTermIndex];
				const previousAcademicTermEndDate = stringToLocalDate(previousAcademicTerm.endDate, "day");
				const daysSincePreviousEnd = Math.abs(dateDiff(today, previousAcademicTermEndDate, "day"));
				const nextAcademicTermStartDate = stringToLocalDate(nextAcademicTerm.startDate, "day");
				const daysToNextStart = Math.abs(dateDiff(nextAcademicTermStartDate, today, "day"));
				if (daysSincePreviousEnd < daysToNextStart) {
					setIndex(previousAcademicTermIndex);
				} else {
					setIndex(nextAcademicTermIndex);
				}
			} else if (
				(defaultSelectedTermFallback === "closest" || defaultSelectedTermFallback === "past") &&
				previousAcademicTermIndex !== null
			) {
				setIndex(previousAcademicTermIndex);
			} else if (
				(defaultSelectedTermFallback === "closest" || defaultSelectedTermFallback === "future") &&
				nextAcademicTermIndex !== null
			) {
				setIndex(nextAcademicTermIndex);
			}
		}

		return { academicTerms, currentAcademicTerm };
	}, [data, setIndex, defaultSelectedTermFallback]);

	const selectedAcademicTerm = (academicTerms[index] ?? null) as
		| AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"]
		| null;
	const nextAcademicTerm = (academicTerms[index + 1] ?? null) as
		| AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"]
		| null;
	const hasNextAcademicTerm = nextAcademicTerm !== null;
	const previousAcademicTerm = (academicTerms[index - 1] ?? null) as
		| AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"]
		| null;
	const hasPreviousAcademicTerm = previousAcademicTerm !== null;

	const selectAcademicTerm = React.useCallback(
		(academicTermID: AcademicTerm["id"]) => {
			const index = academicTerms.findIndex((academicTerm) => academicTerm.id === academicTermID);
			if (index !== -1) {
				setIndex(index);
			}
		},
		[academicTerms, setIndex],
	);

	return {
		loading,
		academicTerms,
		currentAcademicTerm,
		selectedAcademicTerm,
		nextAcademicTerm,
		hasNextAcademicTerm,
		previousAcademicTerm,
		hasPreviousAcademicTerm,
		selectAcademicTerm,
		selectNextAcademicTerm,
		selectPreviousAcademicTerm,
	};
}

export function getCurrentlyEnrolledInAcademicTermFilterInput(academicTermID: AcademicTerm["id"]) {
	return getEnrolledInAcademicTermOnDateFilter(academicTermID);
}

export function toEventCalendarItems(
	academicTerms: NodeOrEdge<Pick<AcademicTerm, "id" | "name" | "startDate" | "endDate">>[] | null | undefined,
) {
	if (!academicTerms) {
		return [];
	}

	const events: EventCalendarItem[] = [];
	for (let i = 0; i < academicTerms.length; i++) {
		const academicTermNodeOrEdge = academicTerms[i];
		const academicTerm = isNodeEdge(academicTermNodeOrEdge) ? academicTermNodeOrEdge.node : academicTermNodeOrEdge;

		events.push({
			id: `${academicTerm.id}-start`,
			name: AcademicTermFormat.name(academicTerm) + " Starts",
			description: "",
			startTime: null,
			endTime: null,
			startDate: academicTerm.startDate,
			endDate: academicTerm.startDate,
			isAllDay: true,
			isPublic: true,
			groups: [],
			_additional: { academicTerm },
		});
		events.push({
			id: `${academicTerm.id}-end`,
			name: AcademicTermFormat.name(academicTerm) + " Ends",
			description: "",
			startTime: null,
			endTime: null,
			startDate: academicTerm.endDate,
			endDate: academicTerm.endDate,
			isAllDay: true,
			isPublic: true,
			groups: [],
			_additional: { academicTerm },
		});
	}

	return events;
}
